import { useState } from 'react'
import {
  Badge,
  Button,
  Input,
  Spinner,
  Tooltip,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components'
import { Add16Regular, ArrowUndo16Regular, Dismiss16Regular } from '@fluentui/react-icons'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { useCreateFaTag, useDeleteFaTag, useFaTags, useUpdateFaTag } from '../hooks/useFocusAreas'
import { useToast } from './ToastProvider'
import { apiErrorMessage } from '../lib/apiError'
import { bayerColors } from '../theme'

const useStyles = makeStyles({
  section: {
    background: '#FFFFFF',
    border: '1px solid #e6e6e6',
    borderRadius: '6px',
    padding: '16px',
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: bayerColors.navy,
    margin: '0 0 4px',
  },
  sub: {
    fontSize: '12px',
    color: '#757575',
    margin: '0 0 12px',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'center',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
  },
  chipInactive: {
    opacity: 0.5,
  },
  addRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    maxWidth: '380px',
  },
  empty: {
    fontSize: '12px',
    color: '#9e9e9e',
    fontStyle: 'italic',
  },
})

/**
 * Manage a focus area's custom tag catalog. Council members manage only their
 * own FA's tags; admins manage any. Non-editors see the active tags read-only.
 * Deleting soft-disables (preserves demand history); disabled tags can be
 * re-enabled. Slots into the FA Workspace.
 */
export function FaTagManager({
  code,
  hideTitle = false,
}: {
  code: string
  /** The workspace folds this panel under its own header; no second title. */
  hideTitle?: boolean
}) {
  const styles = useStyles()
  const toast = useToast()
  const user = useEffectiveUser()
  const canEdit =
    user?.role === 'admin' ||
    user?.role === 'super_admin' ||
    (user?.role === 'council' && user?.focus_area_code === code)

  // Editors need to see disabled tags too (to re-enable them).
  const { data: tags, isLoading } = useFaTags(code, canEdit)
  const createTag = useCreateFaTag()
  const updateTag = useUpdateFaTag()
  const deleteTag = useDeleteFaTag()
  const [newLabel, setNewLabel] = useState('')

  function onError(err: unknown, fallback: string) {
    toast.showToast({ title: apiErrorMessage(err, fallback), intent: 'error' })
  }

  function handleAdd() {
    const label = newLabel.trim()
    if (!label) return
    createTag.mutate(
      { code, body: { label } },
      {
        onSuccess: () => setNewLabel(''),
        onError: (err) => onError(err, 'Could not add tag'),
      },
    )
  }

  const list = tags ?? []
  const visible = canEdit ? list : list.filter((t) => t.active)

  return (
    <section className={styles.section} data-testid="fa-tag-manager">
      {!hideTitle && <h3 className={styles.title}>Tags</h3>}
      <p className={styles.sub}>
        Custom labels for {code} demands.
        {canEdit ? ' Add, disable, or re-enable below.' : ''}
      </p>
      {isLoading ? (
        <Spinner size="tiny" />
      ) : visible.length === 0 ? (
        <div className={styles.empty}>No tags yet{canEdit ? ' — add one below.' : '.'}</div>
      ) : (
        <div className={styles.chips}>
          {visible.map((t) => (
            <span
              key={t.id}
              className={mergeClasses(styles.chip, !t.active && styles.chipInactive)}
              data-testid={`fa-tag-${t.id}`}
            >
              <Badge appearance="tint" color="informative" size="medium">
                {t.label}
              </Badge>
              {canEdit && t.active && (
                <Tooltip content="Disable tag" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<Dismiss16Regular />}
                    aria-label={`Disable ${t.label}`}
                    data-testid={`fa-tag-disable-${t.id}`}
                    onClick={() =>
                      deleteTag.mutate(
                        { code, tagId: t.id },
                        { onError: (err) => onError(err, 'Could not disable tag') },
                      )
                    }
                  />
                </Tooltip>
              )}
              {canEdit && !t.active && (
                <Tooltip content="Re-enable tag" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ArrowUndo16Regular />}
                    aria-label={`Re-enable ${t.label}`}
                    data-testid={`fa-tag-enable-${t.id}`}
                    onClick={() =>
                      updateTag.mutate(
                        { code, tagId: t.id, body: { active: true } },
                        { onError: (err) => onError(err, 'Could not re-enable tag') },
                      )
                    }
                  />
                </Tooltip>
              )}
            </span>
          ))}
        </div>
      )}
      {canEdit && (
        <div className={styles.addRow}>
          <Input
            value={newLabel}
            onChange={(_, d) => setNewLabel(d.value)}
            placeholder="New tag label…"
            aria-label="New tag label"
            data-testid="fa-tag-new-input"
            style={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd()
            }}
          />
          <Button
            appearance="primary"
            icon={<Add16Regular />}
            onClick={handleAdd}
            disabled={!newLabel.trim() || createTag.isPending}
            data-testid="fa-tag-add-btn"
          >
            Add
          </Button>
        </div>
      )}
    </section>
  )
}
